<template>
    <line-with-percentage-graph
        :title="$t('categorySales.title')"
        :percentage-section-title="$t('categorySales.percentageSectionTitle')"
        :line-data="lineData"
        :line-options="lineOptions"
        :area-data="lineData"
        :area-options="areaOptions"
        :pie-data="pieData"
        :pie-total-type="pieTotalType"
        :loading="loading"
        :error="error"
        @redo="getData(true)"
    >
        <template #filters>
            <v-layout column style="flex-grow:0;">
                <v-layout shrink>
                    <v-flex shrink>
                        <select-all
                            ref="categorySelect"
                            class-name="d-inline-block mr-3 mb-3 select--responsive"
                            v-model="category"
                            :items="categoryOptions"
                            :itemsTree="showOnlyTopCategories ? undefined : categoryOptionsTree"
                            :disabled="loading"
                            :label="$t('categorySales.category')"
                            :all-label="$t('categorySales.allCategories')"
                        />
                    </v-flex>
                    <v-flex shrink>
                        <select-all
                            v-if="region !== 'PL'"
                            class-name="d-inline-block mr-3 mb-3 select--responsive"
                            v-model="campaign"
                            :items="campaignOptions"
                            :disabled="loading"
                            :label="$t('categorySales.shop')"
                            :all-label="$t('categorySales.allShops')"
                            require-selection
                            @blur="handleCampaignBlur"
                        />
                    </v-flex>
                    <v-flex shrink>
                        <v-select
                            class="d-inline-block ml-3 mb-3 select--responsive"
                            v-model="dataType"
                            @change="getData()"
                            :items="dataTypeOptions"
                            :disabled="loading"
                            placeholder=" "
                            :label="$t('categorySales.dataType')"
                            outline
                            hide-details
                            dense
                        />
                    </v-flex>
                </v-layout>
                <v-layout shrink>
                    <v-switch
                        v-if="canGroupOthers"
                        v-model="showOnlyTopCategories "
                        :label="$t('filters.showOnlyTopCategories')"
                        :disabled="loading || showSummarized"
                    />

                    <v-switch
                        v-model="showSummarized"
                        :label="$t('filters.showSummarized')"
                        :disabled="loading || showOnlyTopCategories" />
                </v-layout>
            </v-layout>
        </template>
    </line-with-percentage-graph>
</template>

<script>
import { max } from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';
import numberService from '../../../../services/numberService';
import campaignFilterMixin from '../../../../mixins/campaignFilterMixin';
import LineWithPercentageGraph from '../../../graphs/LineWithPercentageGraph.vue';
import SelectAll from '../../../common/SelectAll.vue';
import eventBus from '../../../../services/eventBus';
import categoryFilterMixin from '../../../../mixins/categoryFilterMixin';

export default {
    name: 'category-sales',
    components: {
        SelectAll,
        LineWithPercentageGraph,
    },
    mixins: [
        campaignFilterMixin,
        categoryFilterMixin,
    ],
    props: {
        dateFilter: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            region: process.env.VUE_APP_REGION_MODE,
            data: null,
            dataType: 'quantity',
            dataTypeOptions: [
                {
                    value: 'quantity',
                    text: this.$t('categorySales.quantity'),
                },
                {
                    value: 'amount',
                    text: this.$t('categorySales.amount'),
                },
            ],
            error: null,
            lineData: null,
            pieData: null,
            pieTotalType: 'decimal',
            showSummarized: false,
        };
    },
    computed: {
        loading() {
            return this.quantityChartLoading || this.amountChartLoading;
        },
        lineOptions() {
            let vAxisTitle = this.$t(`categorySales.${this.dataType}`);
            if (this.dataType === 'amount') {
                vAxisTitle += ` [${process.env.VUE_APP_SYSTEM_CURRENCY}]`;
            }

            const options = {
                vAxes: {
                    0: {
                        title: vAxisTitle,
                        viewWindow: {
                            max: !this.hasValueOverZero ? 1 : null,
                        },
                    },
                },
                height: 600,
            };

            if (this.hasValueOverZero) {
                // get all values
                const filteredData = this.data.chart.map(item => Object.keys(item).filter(key => key !== 'date').map(key => item[key]));
                // flatten array and get max value
                const maxValue = max([].concat(...filteredData));
                if ((this.dataType !== 'amount' && maxValue < 1000) || (this.dataType === 'amount' && maxValue === 0)) {
                    options.vAxes[0].format = '#';
                }
            }

            return options;
        },
        areaOptions() {
            return {
                vAxes: {
                    0: {
                        viewWindow: {
                            max: !this.hasValueOverZero ? 1 : null,
                        },
                    },
                },
                height: 600,
            };
        },
        hasValueOverZero() {
            if (this.data && this.data.chart && this.data.chart.length > 0) {
                const hasValueOverZero = this.data.chart.some(item => Object.keys(item).filter(key => key !== 'date').some(key => {
                    if (typeof item[key] === 'object') {
                        return item[key].v > 0;
                    }
                    return item[key] > 0;
                }));
                if (hasValueOverZero) {
                    return true;
                }
            }
            return false;
        },
        ...mapState({
            quantityChartLoading: state => state.reportsCategory.loading.quantityChart,
            amountChartLoading: state => state.reportsCategory.loading.amountChart,
        }),
        ...mapGetters({
            getterQuantityChart: 'reportsCategory/getQuantityChart',
            getterAmountChart: 'reportsCategory/getAmountChart',
        }),
        categoryOptions() {
            return this.canGroupOthers && !this.$refs.categorySelect?.useTreeview ? this.categoryOptionsWithTopCategories : this.getterCategoryOptions;
        },
    },
    watch: {
        dateFilter(newValue, oldValue) {
            if (
                newValue.startDate !== oldValue.startDate
                || newValue.endDate !== oldValue.endDate
                || newValue.compareStartDate !== oldValue.compareStartDate
                || newValue.compareEndDate !== oldValue.compareEndDate
            ) {
                this.getData();
            }
        },
        showOnlyTopCategories() {
            this.getData();
        },
        showSummarized() {
            this.getData();
        },
    },
    created() {
        this.setDefaultCategory();
        this.setDefaultCampaign(['PL']);
        this.getData();
        eventBus.$on('resetFilters', this.reset);
    },
    beforeDestroy() {
        eventBus.$off('resetFilters', this.reset);
    },
    methods: {
        reset() {
            this.setDefaultCategory();
            this.setDefaultCampaign(['PL']);
            this.dataType = 'quantity';
            this.getData();
        },
        emitFilterDefaultChange() {
            const isFilterDefault = this.dataType === 'quantity' && this.isDefaultCategory() && this.isDefaultCampaign(['PL']);
            this.$emit('filterDefaultChange', isFilterDefault);
        },
        getData(force = false) {
            this.emitFilterDefaultChange();
            this.getChart(force);
        },
        handleCampaignBlur() {
            this.getData();
        },
        parseChartData(data, categories) {
            if (!data || data.length === 0) {
                return null;
            }
            const formatType = this.dataType === 'amount' ? 'currency' : 'decimal';
            const parsedData = data.map(item => {
                const dataItem = [this.$d(new Date(item.date), 'short')];
                categories.forEach(key => {
                    dataItem.push({ v: item[key], f: numberService.format(formatType, item[key]) });
                });
                return dataItem;
            });
            return [['Date', ...categories], ...parsedData];
        },
        parsePieChartData(data) {
            if (!data || data.length === 0) {
                return null;
            }
            const formatType = this.dataType === 'amount' ? 'currency' : 'decimal';
            const parsedData = data.map(item => {
                const dataItem = [item[0]];
                dataItem.push({ v: item[1], f: numberService.format(formatType, item[1]) });
                return dataItem;
            });
            return [['Name', 'Value'], ...parsedData];
        },
        async getChart(force = false) {
            try {
                const { startDate, endDate } = this.dateFilter;
                this.error = null;

                if (this.dataType === 'quantity') {
                    await this.fetchQuantityChart({
                        force,
                        startDate,
                        endDate,
                        campaign: this.campaign,
                        category: [],
                    });
                    this.data = this.getterQuantityChart(startDate, endDate, this.campaign, []);
                } else if (this.dataType === 'amount') {
                    await this.fetchAmountChart({
                        force,
                        startDate,
                        endDate,
                        campaign: this.campaign,
                        category: [],
                    });
                    this.data = this.getterAmountChart(startDate, endDate, this.campaign, []);
                }

                this.data = this.filterByCategories(this.data);

                if (this.showOnlyTopCategories && this.canGroupOthers) this.data = this.groupOthers(this.data);
                if (!this.showOnlyTopCategories && this.showSummarized) this.data = this.sumCategories(this.data);

                this.lineData = this.parseChartData(this.data.chart, this.data.chartCategories);
                this.pieData = this.parsePieChartData(this.data.pieChart);
                this.pieTotalType = this.dataType === 'amount' ? 'currency' : 'decimal';
            } catch (e) {
                this.error = e.message;
                this.data = null;
                this.lineData = null;
                this.pieData = null;
            }
        },
        ...mapActions({
            fetchQuantityChart: 'reportsCategory/fetchQuantityChart',
            fetchAmountChart: 'reportsCategory/fetchAmountChart',
        }),
    },
};
</script>

<style lang="scss" scoped>
</style>
